import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFeatureFlagEnabled } from '@posthog/react';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useSessionVerifyMutation } from '@/api/hooks/mutations/use-session-verify-mutation';
import { useGetSessionPreviewQuery } from '@/api/hooks/queries/use-get-session-preview-query';
import { DateInput } from '@/components/DateInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  type SessionStoreState,
  parseSessionData,
} from '@/hooks/use-session-store/session-store-schema';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { getSessionResumePath, useWizardNavigation } from '@/hooks/use-wizard-navigation';
import { trackSessionResumed } from '@/lib/analytics';
import { dateOfBirthSchema } from '@/lib/validation-schemas';
import { getRemainingMilestones } from '@/lib/wizard/wizard';
import type { RoutePath } from '@/lib/wizard/wizard';

const dobFormSchema = z.object({
  dateOfBirth: dateOfBirthSchema,
});

type DobFormData = z.infer<typeof dobFormSchema>;

type ViewState = { view: 'verification' } | { view: 'welcome-back'; nextPath: RoutePath };

/**
 * SessionResume handles session recovery when a user clicks a recovery link.
 *
 * Users visit this page directly via `/session/resume?token=xxx`:
 * 1. Validates the token via preview API
 * 2. Shows DOB verification form on valid token
 * 3. Hydrates all Zustand stores on successful verification
 * 4. Either shows welcome back view (if feature flag enabled) or redirects to resume path
 */
export function SessionResume() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/appLayout/wizardLayout/session/resume' });

  const welcomeBackScreenEnabled = useFeatureFlagEnabled('session-resume-milestone-welcome-screen');
  const [viewState, setViewState] = useState<ViewState>({ view: 'verification' });

  const form = useForm<DobFormData>({
    resolver: zodResolver(dobFormSchema),
    defaultValues: {
      dateOfBirth: '',
    },
  });

  const previewQuery = useGetSessionPreviewQuery(token);

  const verifyMutation = useSessionVerifyMutation({
    onSuccess: (data) => {
      // Validate and parse session data with safe fallbacks for invalid fields
      const sessionData = parseSessionData(data.data);
      hydrateSessionStore(sessionData);

      // Get the resume path
      const resumePath = getSessionResumePath(sessionData);

      trackSessionResumed({
        resume_path: resumePath,
      });

      if (welcomeBackScreenEnabled) {
        setViewState({ view: 'welcome-back', nextPath: resumePath });
      } else {
        navigate({ to: resumePath, replace: true });
      }
    },
    onError: (error) => {
      const isRateLimited = error.message === 'Rate limit exceeded';
      form.setError('dateOfBirth', {
        type: 'manual',
        message: isRateLimited
          ? 'Too many verification attempts. Please wait before trying again.'
          : `We couldn't verify your information. Please try again`,
      });
    },
  });

  useEffect(
    function handleInvalidToken() {
      if (previewQuery.isError) {
        toast.error('Session expired. Please start over.');
        navigate({ to: '/', replace: true });
      }
    },
    [previewQuery.isError, navigate]
  );

  const handleSubmit = (data: DobFormData) => {
    if (!token || !verifyMutation.canSubmit) return;

    verifyMutation.mutate({
      token,
      dob: data.dateOfBirth,
    });
  };

  // Show loading while validating token
  if (previewQuery.isPending) {
    return <SessionLoadingView />;
  }

  // If token is invalid, effect will redirect - show nothing while that happens
  if (previewQuery.isError) {
    return null;
  }

  // Show welcome back view after successful verification (when feature flag enabled)
  if (viewState.view === 'welcome-back') {
    return <WelcomeBackView nextPath={viewState.nextPath} />;
  }

  return (
    <section>
      <Card className="gap-6">
        <CardHeader className="space-y-4">
          <p className="text-charcoal-600 text-lg">Welcome back!</p>
          <CardTitle className="text-2xl leading-tight sm:text-3xl">
            Just a few more questions to complete your order request.
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-8">
          <p className="text-base text-charcoal-900">
            For security purposes, please confirm the patient's birthdate.
          </p>

          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
            <Controller
              name="dateOfBirth"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="recovery-dob">Patient date of birth</FieldLabel>
                  <DateInput {...field} id="recovery-dob" aria-invalid={fieldState.invalid} />
                  {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />

            <Button
              type="submit"
              className="w-full text-lg sm:w-auto sm:min-w-[200px]"
              disabled={verifyMutation.isPending || !verifyMutation.canSubmit}
            >
              {getVerifyButtonContent(verifyMutation)}
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}

function getVerifyButtonContent(mutation: {
  isPending: boolean;
  canSubmit: boolean;
  remainingMs: number;
}) {
  if (mutation.isPending) {
    return (
      <>
        <Loader2 className="size-4 animate-spin" />
        Verifying...
      </>
    );
  }

  if (mutation.canSubmit) {
    return 'Continue';
  }

  return `Try again in ${Math.ceil(mutation.remainingMs / 1000)}s`;
}

function SessionLoadingView() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-charcoal-900">Loading your session...</p>
      </div>
    </div>
  );
}

function WelcomeBackView({ nextPath }: { nextPath: RoutePath }) {
  const navigate = useNavigate();
  const sessionState = useSessionStore();
  const { variant, exitMilestone, resumePath } = useWizardNavigation();

  const remainingMilestones = getRemainingMilestones(sessionState, variant.milestones);

  const handleContinue = () => {
    navigate({ to: nextPath, replace: true });
  };

  // Defensive: If user is in an exit state (e.g., order completed),
  // redirect them to the appropriate exit page.
  if (exitMilestone) {
    return <Navigate to={resumePath} replace />;
  }

  return (
    <section>
      <Card className="gap-6">
        <CardHeader className="space-y-4">
          <p className="text-charcoal-600 text-lg">
            Welcome back{sessionState.patientFirstName ? `, ${sessionState.patientFirstName}` : ''}!
          </p>
          <CardTitle className="text-2xl leading-tight sm:text-3xl">
            Here's what's left to complete your order
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-8">
          {remainingMilestones.length > 0 ? (
            <ol className="space-y-4">
              {remainingMilestones.map((milestone, index) => (
                <li key={milestone.id} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orchid-100 text-lg font-semibold text-orchid-700">
                    {index + 1}
                  </span>
                  <div className="flex flex-col justify-center">
                    <span className="text-lg font-medium text-charcoal-900">{milestone.label}</span>
                    <span className="text-charcoal-600 text-base">{milestone.description}</span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-charcoal-700 text-lg">
              You're almost done! Just one more step to complete your order.
            </p>
          )}

          <Button onClick={handleContinue} className="w-full text-lg sm:w-auto sm:min-w-[200px]">
            Continue
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}

function hydrateSessionStore(data: SessionStoreState) {
  useSessionStore.getState().resetState();
  useSessionStore.getState().updateState(data);
}
